/**
 * The monthly-leakage API surface, in one place.
 * (LEAKAGE_SIGNATURE_SPEC §8, §9.1.)
 *
 * Every call goes through services/httpClient, which owns the base URL and the
 * bearer token. Components never build an API_BASE_URL of their own — the older
 * components/Leakage.js does, and that pattern is legacy: it breaks the moment
 * the deployment's API host changes and it bypasses the token-refresh retry.
 *
 * business_id is never sent. The server takes it from the verified JWT (§3.8);
 * a client that could name its own business could read another restaurant's
 * report.
 */

import http from '../../services/httpClient';

const base = '/leakage';
const enc = encodeURIComponent;

/** The error envelope is {error:{code,message}}; surface the code to callers. */
export function errorOf(err) {
  const payload = err && err.data && err.data.error;
  return {
    code: (payload && payload.code) || 'INTERNAL_ERROR',
    message: (payload && payload.message) || (err && err.message) || 'Something went wrong.',
    status: (err && err.status) || 0,
  };
}

export const leakageApi = {
  months: () => http.get(`${base}/months`),

  eligibility: (month) => http.get(`${base}/${enc(month)}/eligibility`),

  /** Screen 1. Returns the pre-run state (200) when the month is not complete. */
  month: (month) => http.get(`${base}/${enc(month)}`),

  /** Screen 2. */
  category: (month, categoryName) =>
    http.get(`${base}/${enc(month)}/categories/${enc(categoryName)}`),

  /** Screen 3. */
  item: (month, menuItemId) => http.get(`${base}/${enc(month)}/items/${menuItemId}`),

  /** Screen 4, for a dish and for an ingredient. */
  itemHistory: (menuItemId, months = 6) =>
    http.get(`${base}/items/${menuItemId}/history?months=${months}`),
  ingredientHistory: (itemId, months = 6) =>
    http.get(`${base}/ingredients/${itemId}/history?months=${months}`),

  // ── The recall popup ─────────────────────────────────────────────────────
  recallOptions: (month) => http.get(`${base}/${enc(month)}/recalls/options`),
  recallPreview: (month, entry) => http.post(`${base}/${enc(month)}/recalls/preview`, entry),
  saveRecalls: (month, entries) => http.post(`${base}/${enc(month)}/recalls`, { entries }),

  // ── The run ──────────────────────────────────────────────────────────────
  run: (month, declareEmpty = false) =>
    http.post(`${base}/${enc(month)}/run`, { declare_empty: declareEmpty }),
  status: (runId) => http.get(`${base}/runs/${runId}/status`),
  // `reason` is optional: the server records one when the caller has none, so a
  // one-press Recalculate keeps the audit trail without a box to fill in.
  recalculate: (month, reason) => http.post(
    `${base}/${enc(month)}/recalculate`, reason ? { reason } : {}
  ),

  // ── Actions that close the loop ──────────────────────────────────────────
  calibratePreview: ({ menuItemId, itemId, newQuantity, newUnitId }) => {
    const params = new URLSearchParams({
      menu_item_id: String(menuItemId),
      item_id: String(itemId),
    });
    if (newQuantity !== undefined && newQuantity !== null) params.set('new_quantity', String(newQuantity));
    if (newUnitId !== undefined && newUnitId !== null) params.set('new_unit_id', String(newUnitId));
    return http.get(`${base}/calibrate/preview?${params.toString()}`);
  },
  calibrate: (body) => http.post(`${base}/calibrate`, body),

  createSpotCheck: (body) => http.post(`${base}/spot-checks`, body),
  recordSpotCheck: (id, samples) => http.patch(`${base}/spot-checks/${id}`, { samples }),

  // ── Phase 0 ──────────────────────────────────────────────────────────────
  prepYield: () => http.get(`${base}/prep-yield`),
  reviewPrepYield: (id, body) => http.patch(`${base}/prep-yield/${id}`, body),
  mappingIntegrity: () => http.get(`${base}/mapping-integrity`),

  // -- Weekly A-tier counts (Section 11) -----------------------------------
  aTier: () => http.get(`${base}/a-tier`),
  saveATierCount: (body) => http.post(`${base}/a-tier/count`, body),
};

// ── Shared formatting ───────────────────────────────────────────────────────
// The same rules as the server's verdict templates (§9.7), so a number printed
// beside a sentence never disagrees with the sentence.

/** ₹48,600 — Indian grouping, no paise. */
export const inr = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;

/**
 * 31.0% — and 1,17,704% rather than 117704.4%.
 *
 * leak_pct is a ratio, not a share: a month with barely any sales and real
 * stock movement genuinely reads as 1177x expected. A tenth of a percent is
 * meaningless at that size and the ungrouped digits read as a broken number, so
 * past 1000% the decimal is dropped and the figure is grouped like every other
 * number on the screen. Everything under that is unchanged.
 */
export const pct = (value, dp = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const p = Number(value) * 100;
  if (!Number.isFinite(p)) return '—';
  return Math.abs(p) >= 1000
    ? `${Math.round(p).toLocaleString('en-IN')}%`
    : `${p.toFixed(dp)}%`;
};

/** "12 Aug" / "1 Sep 2026" — short, and never a raw ISO string. */
export function shortDate(value, withYear = false) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}),
  });
}

/** '2026-08' → 'August 2026'. */
export function monthLabel(month) {
  if (!month) return '';
  const [y, m] = String(month).split('-').map(Number);
  if (!y || !m) return String(month);
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function monthShort(month) {
  if (!month) return '';
  const [y, m] = String(month).split('-').map(Number);
  if (!y || !m) return String(month);
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
}

export const addMonths = (month, delta) => {
  const [y, m] = String(month).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export const currentMonth = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * Verdict text arrives with **bold** markers around the number that matters.
 * Rendering it as segments keeps the emphasis without ever putting server text
 * through dangerouslySetInnerHTML.
 */
export function verdictSegments(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.filter(Boolean).map((part, index) => (
    part.startsWith('**') && part.endsWith('**')
      ? { key: index, bold: true, text: part.slice(2, -2) }
      : { key: index, bold: false, text: part }
  ));
}

/**
 * Colour by MEANING, never by decoration (§9.2):
 *   red    money lost / stock missing
 *   amber  serving size
 *   grey   normal, under-serving, data issue
 * Never green for "no leak" — the absence of red is the signal.
 */
export function toneForCause(causeCode) {
  switch (causeCode) {
    case 'FIXED_LOSS': return 'loss';
    case 'PORTION_DRIFT': return 'warn';
    case 'UNDER_POUR': return 'muted';
    case 'DATA_ISSUE': return 'muted';
    default: return 'muted';
  }
}

export default leakageApi;
